import { AxiosInstance } from 'axios';
import { getScimConfig } from '../utils/envUtils';
import { getAxiosInstance } from './axiosInstance';

export interface FilterValidationResult {
    originalFilter: string;
    isValid: boolean;
    validationErrors: string[];
    normalizedFilter: string;
    testResults: {
        sampleDataCount: number;
        matchingRecords: number;
        executionTimeMs: number;
        generatedSql: string;
    };
    generatedSql?: string;
}

export interface FilterTestResult {
    testName: string;
    inputFilter: string;
    generatedSql: string;
    isValid: boolean;
    expectedResult: string;
}

export interface FilterTestResponse {
    totalTests: number;
    passedTests: number;
    failedTests: number;
    results: FilterTestResult[];
}

export interface SqlTestResult {
    testName: string;
    inputFilter: string;
    generatedSql: string;
    expectedSqlPattern: string;
    expectedParameters: string[];
    isValid: boolean;
    sqlValidation: {
        isValid: boolean;
        errors: string[];
    };
}

export interface SqlTestResponse {
    totalTests: number;
    passedTests: number;
    failedTests: number;
    results: SqlTestResult[];
}

export interface FilterSyntaxExample {
    name: string;
    filter: string;
    description: string;
}

export class ScimTestingService {
    private api: AxiosInstance;
    private baseUrl: string;

    constructor() {
        const scimConfig = getScimConfig();
        this.baseUrl = scimConfig.apiUrl;
        this.api = getAxiosInstance(this.baseUrl);
    }


    /**
     * Validate a SCIM filter string
     */
    async validateFilter(filter: string): Promise<FilterValidationResult> {
        try {
            const response = await this.api.post('/api/Testing/validate-filter', {
                filter
            });

            return {
                ...response.data,
                generatedSql: response.data.generatedSql || '-- SQL generation not available'
            };
        } catch (error) {
            console.error('Error validating filter:', error);
            throw error;
        }
    }

    /**
     * Test filter generation with multiple test cases
     */
    async testFilterGeneration(testCases: Array<{
        name: string;
        filter: string;
        expectedResult: string;
    }>): Promise<FilterTestResponse> {
        try {
            const response = await this.api.post('/api/Testing/test-filter-generation', {
                testCases
            });

            return response.data;
        } catch (error) {
            console.error('Error testing filter generation:', error);
            throw error;
        }
    }

    /**
     * Get filter syntax examples
     */
    async getFilterSyntaxExamples(): Promise<FilterSyntaxExample[]> {
        try {
            const response = await this.api.get('/api/Testing/filter-syntax-examples');

            return response.data;
        } catch (error) {
            console.error('Error getting filter syntax examples:', error);
            throw error;
        }
    }

    /**
     * Test SQL generation with multiple test cases
     */
    async testSqlGeneration(testCases: Array<{
        name: string;
        filter: string;
        expectedSqlPattern: string;
        expectedParameters: string[];
    }>): Promise<SqlTestResponse> {
        try {
            const response = await this.api.post('/api/Testing/test-sql-generation', {
                testCases: testCases
            });

            return response.data;
        } catch (error) {
            console.error('Error testing SQL generation:', error);
            throw error;
        }
    }
}
